/*
  AoA header behaviour (Sistema de diseño AoA; mockup initMega / initMobileMenu). Loaded with defer
  from sections/header.liquid. Liquid renders everything; this file only enhances:

  Desktop panels (from 1068px, [data-aoa-mega-item]: AoA mega panels and theme dropdowns):
  - CSS alone opens them on hover and focus. With this file, the class .is-open takes over.
  - Mouse: open on pointer enter, close 180ms after the pointer leaves (the gap to the panel does
    not close them).
  - Keyboard (WAI fly-out menus: "submenus should not open when using the tab key"): focus on the top
    link does not open anything, so Tab goes from Men to Women. The toggle button after each top link
    ([data-aoa-sub-toggle], visible only on keyboard focus) opens and closes the panel and carries
    aria-expanded / aria-controls. Focus leaving the item closes it.
  - Touch (tablets from 1068px): the first tap on a top link opens its panel, the second follows the
    link; a tap outside closes it.
  - Esc closes the open panel (hover or keyboard, WCAG 1.4.13) and returns focus to its toggle.

  Mobile menu (#AoaMobileMenu, up to 1067px):
  - Modal <dialog> opened with showModal(): top layer, the rest of the page inert, Tab kept inside,
    Esc closes (native). Here: aria-expanded on the trigger, scroll lock on <html>, focus on the close
    button when it opens and back on the trigger when it closes, and closing from 1068px.
  - Tapped before this deferred file runs, the trigger's command="show-modal" opens it natively and
    the state is picked up here. Without JS the :target link of header-mobile-toggle.liquid opens it.

  Theme editor: everything re-binds on shopify:section:load and is released on unload; selecting a
  "Mega menu" block (shopify:block:select) opens its panel.
*/
(function () {
  'use strict';

  if (window.aoaHeader) return;
  window.aoaHeader = true;

  var CLOSE_DELAY = 180;
  var desktop = window.matchMedia('(min-width: 1068px)');
  var LOCK_CLASS = 'aoa-menu-lock';

  /* ---------- Mega panels ---------- */
  function MegaMenu(nav) {
    this.nav = nav;
    this.header = nav.closest('.aoa-header');
    this.items = [];
    this.timer = null;
    this.openItem = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocPointerDown = this.onDocPointerDown.bind(this);

    Array.prototype.forEach.call(nav.querySelectorAll('[data-aoa-mega-item]'), function (item, index) {
      var link = item.querySelector(':scope > a');
      var panel = item.querySelector(':scope > .sub-menu');
      if (!link || !panel) return;
      if (!panel.id) panel.id = 'AoaMegaPanel-auto-' + (index + 1);
      var toggle = item.querySelector(':scope > [data-aoa-sub-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-controls', panel.id);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.hidden = false;
        toggle.addEventListener('click', this.onToggle.bind(this, item));
      }
      item.addEventListener('pointerenter', this.onEnter.bind(this, item));
      item.addEventListener('pointerleave', this.onLeave.bind(this));
      item.addEventListener('pointerdown', this.onPointerDown.bind(this));
      item.addEventListener('focusout', this.onFocusOut.bind(this, item));
      link.addEventListener('click', this.onLinkClick.bind(this, item));
      this.items.push(item);
    }, this);
    if (!this.items.length) return;

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('pointerdown', this.onDocPointerDown, { passive: true });
    nav.setAttribute('data-aoa-mega-js', '');
  }

  MegaMenu.prototype.onEnter = function (item, event) {
    if (event.pointerType === 'touch') return;
    this.open(item);
  };

  MegaMenu.prototype.onLeave = function (event) {
    if (event.pointerType === 'touch') return;
    this.closeLater();
  };

  MegaMenu.prototype.onPointerDown = function (event) {
    this.pointerType = event.pointerType;
  };

  // Touch has no hover: the first tap on a top link opens its panel, the second follows the link
  MegaMenu.prototype.onLinkClick = function (item, event) {
    var touch = this.pointerType === 'touch';
    this.pointerType = null;
    if (!touch || this.openItem === item) return;
    event.preventDefault();
    item.removeAttribute('data-aoa-mega-dismissed');
    this.open(item);
  };

  // A tap outside the open item closes it
  MegaMenu.prototype.onDocPointerDown = function (event) {
    if (event.pointerType !== 'touch' || !this.openItem || this.openItem.contains(event.target)) return;
    this.close();
  };

  MegaMenu.prototype.onToggle = function (item) {
    if (this.openItem === item) {
      this.close();
    } else {
      item.removeAttribute('data-aoa-mega-dismissed');
      this.open(item);
    }
  };

  MegaMenu.prototype.onFocusOut = function (item, event) {
    if (event.relatedTarget && item.contains(event.relatedTarget)) return;
    if (this.openItem === item) this.close();
  };

  MegaMenu.prototype.open = function (item) {
    clearTimeout(this.timer);
    // Dismissed with Esc under the pointer: stays closed until the pointer or the focus leaves the item
    if (this.openItem === item || item.hasAttribute('data-aoa-mega-dismissed')) return;
    if (this.openItem) this.setState(this.openItem, false);
    this.openItem = item;
    this.setState(item, true);
  };

  MegaMenu.prototype.closeLater = function () {
    clearTimeout(this.timer);
    this.timer = setTimeout(this.close.bind(this), CLOSE_DELAY);
  };

  MegaMenu.prototype.close = function () {
    clearTimeout(this.timer);
    if (!this.openItem) return;
    this.setState(this.openItem, false);
    this.openItem = null;
  };

  MegaMenu.prototype.setState = function (item, open) {
    item.classList.toggle('is-open', open);
    // Solid header while a panel is open (transparent header on the home, assets/aoa-header.css)
    if (this.header) this.header.classList.toggle('aoa-header--panel-open', open);
    var toggle = item.querySelector(':scope > [data-aoa-sub-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  };

  MegaMenu.prototype.onKeydown = function (event) {
    if (event.key !== 'Escape' || !this.openItem) return;
    var item = this.openItem;
    var focusInside = item.contains(document.activeElement);
    var hovered = item.matches(':hover');
    this.close();
    var target = item.querySelector(':scope > [data-aoa-sub-toggle]') || item.querySelector(':scope > a');
    if (focusInside && target) target.focus();
    // Only a panel dismissed under the pointer stays closed (focus alone never opens one): closed from
    // the keyboard, the next pointer hover opens it again
    if (!hovered) return;
    item.setAttribute('data-aoa-mega-dismissed', '');
    var clear = function () {
      item.removeAttribute('data-aoa-mega-dismissed');
      item.removeEventListener('pointerleave', clear);
      item.removeEventListener('focusout', clear);
    };
    item.addEventListener('pointerleave', clear);
    item.addEventListener('focusout', clear);
  };

  MegaMenu.prototype.destroy = function () {
    clearTimeout(this.timer);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('pointerdown', this.onDocPointerDown);
    if (this.header) this.header.classList.remove('aoa-header--panel-open');
  };

  /* ---------- Mobile menu ---------- */
  function MobileMenu(dialog) {
    this.dialog = dialog;
    this.triggers = Array.prototype.slice.call(document.querySelectorAll('[data-aoa-menu-open]'));
    this.closeButton = dialog.querySelector('[data-aoa-menu-close]');
    this.opener = null;
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onBreakpoint = this.onBreakpoint.bind(this);
    this.onLinkClick = this.onLinkClick.bind(this);

    this.triggers.forEach(function (trigger) {
      trigger.addEventListener('click', this.onTriggerClick);
    }, this);
    if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
    this.onKeydown = this.onKeydown.bind(this);
    dialog.addEventListener('close', this.onClose);
    dialog.addEventListener('click', this.onLinkClick);
    dialog.addEventListener('keydown', this.onKeydown);
    if (desktop.addEventListener) desktop.addEventListener('change', this.onBreakpoint);
    // Opened natively (command="show-modal") before this file loaded
    if (dialog.open) this.onOpen(this.triggers[0]);
  }

  MobileMenu.prototype.onTriggerClick = function (event) {
    // Take over the native command so the state below is always set
    event.preventDefault();
    this.open(event.currentTarget);
  };

  MobileMenu.prototype.open = function (opener) {
    if (this.dialog.open) return;
    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
    this.onOpen(opener);
  };

  MobileMenu.prototype.onOpen = function (opener) {
    this.opener = opener || null;
    document.documentElement.classList.add(LOCK_CLASS);
    this.triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    });
    if (this.closeButton) this.closeButton.focus({ preventScroll: true });
  };

  MobileMenu.prototype.onCloseClick = function (event) {
    event.preventDefault();
    this.close();
  };

  MobileMenu.prototype.close = function () {
    if (!this.dialog.open) return;
    if (typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
      this.onClose();
    }
  };

  // Every way out (close button, Esc, breakpoint, link) ends here
  MobileMenu.prototype.onClose = function () {
    document.documentElement.classList.remove(LOCK_CLASS);
    this.triggers.forEach(function (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    });
    var target = this.opener || this.triggers[0];
    this.opener = null;
    if (!this.leaving && target && target.getClientRects().length) target.focus({ preventScroll: true });
    this.leaving = false;
  };

  // Tab wraps inside the menu instead of leaving to the browser toolbar (same rule as the search drawer)
  MobileMenu.prototype.onKeydown = function (event) {
    if (event.key !== 'Tab') return;
    var focusable = Array.prototype.filter.call(
      this.dialog.querySelectorAll('a[href], button:not([disabled]), summary, input, select, [tabindex]:not([tabindex="-1"])'),
      function (el) {
        return el.getClientRects().length > 0; // skips closed accordions and the no-JS link
      }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  MobileMenu.prototype.onBreakpoint = function () {
    if (desktop.matches) this.close();
  };

  // Following a link: no focus jump back to the header while the next page loads
  MobileMenu.prototype.onLinkClick = function (event) {
    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.getAttribute('href').charAt(0) === '#') return;
    this.leaving = true;
  };

  MobileMenu.prototype.destroy = function () {
    this.triggers.forEach(function (trigger) {
      trigger.removeEventListener('click', this.onTriggerClick);
    }, this);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onCloseClick);
    this.dialog.removeEventListener('close', this.onClose);
    this.dialog.removeEventListener('click', this.onLinkClick);
    this.dialog.removeEventListener('keydown', this.onKeydown);
    if (desktop.removeEventListener) desktop.removeEventListener('change', this.onBreakpoint);
    document.documentElement.classList.remove(LOCK_CLASS);
  };

  /* ---------- Setup ---------- */
  var instances = [];

  function init() {
    instances.forEach(function (instance) {
      instance.destroy();
    });
    instances = [];
    document.querySelectorAll('.aoa-header .full-menu').forEach(function (nav) {
      instances.push(new MegaMenu(nav));
    });
    var dialog = document.getElementById('AoaMobileMenu');
    if (dialog) instances.push(new MobileMenu(dialog));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target.querySelector && event.target.querySelector('.aoa-header')) init();
  });
  // Theme editor: selecting a "Mega menu" block opens its panel; deselecting closes it
  function onBlockSelect(event) {
    var panel = event.target.closest && event.target.closest('[data-aoa-mega-panel]');
    if (!panel) return;
    instances.forEach(function (instance) {
      if (!instance.items) return;
      instance.items.forEach(function (item) {
        if (!item.contains(panel)) return;
        item.removeAttribute('data-aoa-mega-dismissed');
        if (event.type === 'shopify:block:select') {
          instance.open(item);
        } else if (instance.openItem === item) {
          instance.close();
        }
      });
    });
  }
  document.addEventListener('shopify:block:select', onBlockSelect);
  document.addEventListener('shopify:block:deselect', onBlockSelect);

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelector && event.target.querySelector('.aoa-header')) {
      instances.forEach(function (instance) {
        instance.destroy();
      });
      instances = [];
    }
  });
})();
